import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import sortBy from 'lodash/sortBy';
import styled from 'styled-components';
import { Resident } from '../../../../components/organisms/ResidentFilters/types';
import defaultAvatar from 'images/avatar.svg';

const CarouselWrap = styled.div`
  position: relative;
`;

const Track = styled.div`
  display: flex;
  overflow-x: auto;
  gap: 0.5rem;
  padding: 0 1rem;
  scroll-snap-type: none;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Card = styled.div`
  min-width: 8rem;
  // height: 9.9375rem;
  border: 1px solid #e9e7e4;
  border-radius: 0.75rem;
  overflow: hidden;
  background: #ffffff;
  scroll-snap-align: start;
`;

const CardImage = styled.img`
  width: 100%;
  height: 5rem;
  object-fit: cover;
  display: block;
`;

const CardBody = styled.div`
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const CardName = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #232323;
`;

const CardMeta = styled.div`
  font-size: 0.875rem;
  color: #232323;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ScrollButton = styled.button<{ $pos: 'left' | 'right' }>`
  position: absolute;
  ${({ $pos }) => ($pos === 'left' ? 'left: 0.5rem;' : 'right: 0.5rem;')}
  top: 50%;
  transform: translateY(-60%);
  border: 1px solid #e6e6e6;
  background: #ffffff;
  border-radius: 999px;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);

  svg {
    width: 1rem;
    height: 1rem;
    transform: ${({ $pos }) => ($pos === 'left' ? 'rotate(180deg)' : 'none')};
  }
`;

function CaretRight() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6 12L10 8L6 4"
        stroke="#57534E"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function getAttributeLabel(resident: Resident) {
  const attributes = resident.attributes;

  if (!attributes) return null;

  if (attributes.dog) return 'Dog Owner';
  if (attributes.cat) return 'Cat Owner';
  if (attributes.commuter) return 'Commuter';
  if (attributes.remote_worker) return 'Works remote';
  if (attributes.parent) return 'Parent';
  if (attributes.pet_other) return 'Pet Owner';

  return null;
}

function getBedLabel(resident: Resident) {
  const count = resident.attributes?.bed_count;

  return count ? `${count} Bed` : null;
}

type Props = {
  residents: Resident[];
};

export function ResidentCarousel({ residents }: Readonly<Props>) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const hasScrolled = useRef(false);
  const [showNext, setShowNext] = useState(false);
  const [showPrev, setShowPrev] = useState(false);

  const items = useMemo(
    () => sortBy(residents, (r) => !r.image_url),
    [residents]
  );

  const updateButtons = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const maxScroll = el.scrollWidth - el.clientWidth;

    if (maxScroll <= 0) {
      setShowPrev(false);
      setShowNext(false);
      return;
    }

    const scrollLeft = Math.round(el.scrollLeft);
    setShowNext(scrollLeft < maxScroll - 1);
    setShowPrev(hasScrolled.current && scrollLeft > 0);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    el.scrollLeft = 0;

    const raf = requestAnimationFrame(() => updateButtons());

    const onScroll = () => {
      if (Math.round(el.scrollLeft) > 0) {
        hasScrolled.current = true;
      }
      updateButtons();
    };
    el.addEventListener('scroll', onScroll);
    window.addEventListener('resize', updateButtons);

    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateButtons);
    };
  }, [updateButtons]);

  const handleNext = () => {
    const el = scrollRef.current;
    if (!el) return;

    hasScrolled.current = true;
    el.scrollBy({ left: el.clientWidth, behavior: 'smooth' });
  };

  const handlePrev = () => {
    const el = scrollRef.current;
    if (!el) return;

    el.scrollBy({ left: -el.clientWidth, behavior: 'smooth' });
  };

  if (!items.length) return null;

  return (
    <CarouselWrap>
      <Track ref={scrollRef}>
        {items.map((resident) => (
          <Card key={resident.id}>
            <CardImage
              src={resident.image_url ?? defaultAvatar}
              alt={resident.first_name}
            />
            <CardBody>
              <CardName>{resident.first_name}</CardName>
              <CardMeta>{getBedLabel(resident)}</CardMeta>
              <CardMeta>{getAttributeLabel(resident)}</CardMeta>
            </CardBody>
          </Card>
        ))}
      </Track>
      {showPrev && (
        <ScrollButton
          $pos="left"
          onClick={handlePrev}
          aria-label="Previous residents"
        >
          <CaretRight />
        </ScrollButton>
      )}
      {showNext && (
        <ScrollButton
          $pos="right"
          onClick={handleNext}
          aria-label="Next residents"
        >
          <CaretRight />
        </ScrollButton>
      )}
    </CarouselWrap>
  );
}
